import { ValidationError } from '@/api/types';
import { useToast } from '@/hooks/useToast';
import { t } from 'i18next';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');

const isValidationError = (value: unknown): value is ValidationError =>
  typeof value === 'object' &&
  value !== null &&
  (isStringArray((value as ValidationError).errors) ||
    Array.isArray((value as ValidationError).children));

export const getApiBadRequestErrorsToString = (
  errors: ValidationError[],
): string => {
  if (!Array.isArray(errors)) {
    return '';
  }

  return errors.reduce((a, e) => {
    let message = isStringArray(e?.errors) ? e.errors.join(' ') : '';

    if (Array.isArray(e?.children) && e.children.length > 0) {
      message = `${message} ${getApiBadRequestErrorsToString(e.children)}`;
    }

    return `${a} ${message}`;
  }, '');
};

export const getApiErrorMessageToString = (message: unknown): string => {
  if (typeof message === 'string') {
    return message;
  }

  if (Array.isArray(message)) {
    return message
      .map((entry) =>
        isValidationError(entry)
          ? getApiBadRequestErrorsToString([entry])
          : String(entry),
      )
      .join(' ')
      .trim();
  }

  return '';
};

const ERROR_TOKEN_TRANSLATION_KEYS: Record<string, string> = {
  'entra-user-status-locked': 'error.entraUserStatusLocked',
  'password-recently-used': 'error.passwordRecentlyUsed',
  'password-policy-not-met': 'error.passwordPolicyNotMet',
};

export const handleAxiosError = (
  e: any,
  toast: ReturnType<typeof useToast>,
) => {
  const tokenTranslationKey =
    ERROR_TOKEN_TRANSLATION_KEYS[e?.message] ??
    (typeof e?.errors === 'string'
      ? ERROR_TOKEN_TRANSLATION_KEYS[e.errors]
      : undefined);

  if (tokenTranslationKey) {
    toast?.error(t('toast.error'), t(tokenTranslationKey));
    return;
  }

  let message = t('error.message', {
    message: e.message,
  });

  if (e.response) {
    message = t('error.message', {
      message:
        getApiErrorMessageToString(e.response.data?.message) || e.message,
    });
  } else if (e.errors) {
    message = t('error.message', {
      message: getApiErrorMessageToString(e.errors) || e.message,
    });
  }

  toast?.error(
    t('toast.error'),
    t('error.processRequest', {
      message,
    }),
  );
};
